/* eslint-disable no-redeclare */
import { toArray, closest } from '@nrk/dh-basis-common'
import type {
  LayerAnimation,
  Easing,
  PropertyValues,
  PropertyType,
  AnimationProperty,
} from '../../types/prosentvis-bilde.d.ts'
import './prosentvis-bilde.module.css'

const SYNTHETIC_DURATION = 10_000

export default class ProsentvisBilde extends HTMLElement {
  connectedCallback() {
    this.#updateSizes()

    const animasjonsscroll = this.closest('dh-animasjonsscroll')
    if (animasjonsscroll) {
      this.#animateLayers(
        // @ts-ignore
        animasjonsscroll.timeline,
        // @ts-ignore
        animasjonsscroll.rangeStart,
        // @ts-ignore
        animasjonsscroll.rangeEnd,
        // @ts-ignore
        animasjonsscroll.defaultProgress,
      )
    }
  }

  #isFullWidth() {
    return (
      closest(this, 'article')?.classList?.contains('article-feature') &&
      closest(this, '.remoterenderedcontent-reference')?.classList.contains('fc')
    )
  }

  #updateSizes() {
    if (this.#isFullWidth()) {
      this.layers.forEach((elm) => elm.setAttribute('sizes', '100vw'))
    }
  }

  #animateLayers(timeline, rangeStart, rangeEnd, defaultProgress) {
    if (!('Animation' in window)) {
      return
    }
    // Currently we need to set up one animation per property per layer.
    // In the future is should be possible to combine these keyframe effects into one grouped effect.
    // And use only one animation to drive that effect.
    //
    // TODO: Switch to GroupEffect once supported (for easier debugging)
    for (const layer of this.layers) {
      const layerAnim = JSON.parse(layer.getAttribute('data-layer-animation'))
      const keyframeEffects = toKeyframeEffects(layerAnim, layer)

      // TODO: Switch to GroupEffect once supported (for easier debugging)
      /*layer.animate(keyframes, {
        timeline,
        rangeStart,
        rangeEnd,
        fill: 'both',
        composite: i === 0 ? 'replace' : 'accumulate',
      })*/
      for (const keyframeEffect of keyframeEffects) {
        if (timeline) {
          // eslint-disable-next-line compat/compat
          const animation = new Animation(keyframeEffect, timeline)
          // @ts-ignore
          animation.rangeStart = rangeStart
          // @ts-ignore
          animation.rangeEnd = rangeEnd
          animation.play()
        } else {
          // When user prefers reduced motion no timeline is available.
          // Set the progress to default progress
          // eslint-disable-next-line compat/compat
          const animation = new Animation(keyframeEffect)
          animation.currentTime = defaultProgress * SYNTHETIC_DURATION
        }
      }
    }
  }

  get layers() {
    return toArray(this.querySelectorAll('[data-layer-animation]'))
  }

  get progress() {
    return parseFloat(this.getAttribute('data-progress') || '0.0')
  }

  set progress(val) {
    this.setAttribute('data-progress', val.toFixed(4))
  }
}

function toKeyframe<T extends PropertyType>(
  type: T,
  value: PropertyValues[T],
  offset: number,
  easing: string,
): Keyframe {
  const keyframe = { offset, easing }
  switch (type) {
    case 'rotate':
      return { ...keyframe, rotate: `${value}deg` }
    case 'scale':
      return { ...keyframe, scale: `${value}` }
    case 'translateX':
      return { ...keyframe, translate: `${value}% 0%`, composite: 'add' }
    case 'translateY':
      return { ...keyframe, translate: `0% ${value}%`, composite: 'add' }
    case 'translate':
      return {
        ...keyframe,
        translate: `${(value as PropertyValues['translate']).x}% ${(value as PropertyValues['translate']).y}%`,
        composite: 'add',
      }
    case 'opacity':
      return { ...keyframe, opacity: value as number }
  }
}

function toEasingString(easing: Easing): string {
  switch (easing.type) {
    case 'ease':
    case 'ease-in':
    case 'ease-out':
    case 'ease-in-out':
    case 'linear':
      return easing.type
    case 'steps':
      return `steps(${easing.n}, ${easing.jumpterm})`
    case 'cubic-bezier':
      return `cubic-bezier(${easing.x1}, ${easing.y1}, ${easing.x2}, ${easing.y2})`
    default:
      return 'none'
  }
}

function toKeyframeEffects(animation: LayerAnimation, target: Element): KeyframeEffect[] {
  return animation.map(<T extends PropertyType>(property: AnimationProperty<T>) => {
    // TODO: Switch to one KeyframeEffect per property once GroupEffect is supported

    const keyframes = property.keyframes.map((keyframe) => {
      return toKeyframe(
        property.type,
        keyframe.value,
        keyframe.percent / 100,
        toEasingString(keyframe.easing),
      )
    })
    // eslint-disable-next-line compat/compat
    return new KeyframeEffect(target, keyframes, { fill: 'both', duration: SYNTHETIC_DURATION })
  })
}
